import { HUGO_CONFIG_FILENAMES, detectSourceBuild } from "@mph/source-build";

const HUGO_CONFIG_PATHS = new Set<string>(HUGO_CONFIG_FILENAMES);
const SOURCE_GENERATED_ROOTS = new Set([".next", "out", "dist", "build"]);

export const MAX_UPLOAD_FILE_BYTES = 1024 * 1024;
export const MAX_UPLOAD_TOTAL_BYTES = 5 * 1024 * 1024;
export const LARGE_UNKNOWN_FOLDER_BYTES = 25 * 1024 * 1024;

export const OMITTED_SEGMENTS = new Set([
  ".git",
  ".env",
  ".DS_Store",
  ".netrc",
  ".npmrc",
  ".pypirc",
  ".yarnrc",
  ".yarnrc.yml",
  "node_modules",
  ".wrangler",
]);

export interface SelectedFile {
  file: File;
  path: string;
}

export interface FolderUploadSummary {
  totalBytes: number;
  ignoredBytes: number;
  uploadBytes: number;
  largeFolders: Array<{ path: string; bytes: number }>;
  oversizedFiles: Array<{ path: string; bytes: number }>;
  blocked: boolean;
}

export interface SafeFileSelection {
  included: SelectedFile[];
  omitted: string[];
  omittedFiles: SelectedFile[];
  totalBytes: number;
}

export interface FolderUploadPlan {
  files: SelectedFile[];
  omitted: string[];
  summary: FolderUploadSummary;
}

export interface FolderDiagnosis {
  kind: "plain-static" | "prebuilt-static" | "source-static" | "source-runtime" | "unsupported";
  label: string;
  publishRoot: string;
  explanation: string;
  connections?: string[];
}

export function projectRelativePath(file: File): string {
  const relative = file.webkitRelativePath || file.name;
  const segments = relative.split("/").filter(Boolean);
  return segments.length > 1 ? segments.slice(1).join("/") : segments.join("/");
}

export function shouldOmit(path: string): boolean {
  const segments = path.split("/");
  return segments.some((segment) => {
    if (OMITTED_SEGMENTS.has(segment)) return true;
    return segment.startsWith(".env.") && segment !== ".env.example";
  });
}

export function selectSafeFiles(files: FileList | File[]): SafeFileSelection {
  const included: SelectedFile[] = [];
  const omitted: string[] = [];
  const omittedFiles: SelectedFile[] = [];
  let totalBytes = 0;

  for (const file of Array.from(files)) {
    const path = projectRelativePath(file);
    const displayPath = path || file.name;
    totalBytes += file.size;
    if (!path || path.includes("..") || path.startsWith("/")) {
      omitted.push(displayPath);
      omittedFiles.push({ file, path: displayPath });
    } else if (shouldOmit(path)) {
      omitted.push(path);
      omittedFiles.push({ file, path });
    } else {
      included.push({ file, path });
    }
  }

  return {
    included: included.sort((a, b) => a.path.localeCompare(b.path)),
    omitted: omitted.sort(),
    omittedFiles: omittedFiles.sort((a, b) => a.path.localeCompare(b.path)),
    totalBytes,
  };
}

export function planFolderUpload(
  selection: SafeFileSelection,
  diagnosis: FolderDiagnosis,
  finalizedFiles?: SelectedFile[],
): FolderUploadPlan {
  const rootedFiles = diagnosis.kind === "unsupported"
    ? selection.included
    : filesForPublish(selection.included, diagnosis.publishRoot);
  const candidateFiles = diagnosis.kind === "source-static" || diagnosis.kind === "source-runtime"
    ? rootedFiles.filter(({ path }) => !SOURCE_GENERATED_ROOTS.has(path.split("/")[0] ?? ""))
    : rootedFiles;
  const files = [...(finalizedFiles ?? candidateFiles)].sort((left, right) => left.path.localeCompare(right.path));
  const uploadedFiles = new Set(files.map(({ file }) => file));
  const omittedFiles = [
    ...selection.omittedFiles,
    ...selection.included.filter(({ file }) => !uploadedFiles.has(file)),
  ];
  const omitted = [...new Set(omittedFiles.map(({ path }) => path))].sort();
  const uploadBytes = files.reduce((total, { file }) => total + file.size, 0);
  const folderBytes = new Map<string, number>();
  for (const { file, path } of files) {
    const segments = path.split("/");
    if (segments.length < 2) continue;
    const root = segments[0]!;
    folderBytes.set(root, (folderBytes.get(root) ?? 0) + file.size);
  }
  const largeFolders = [...folderBytes]
    .filter(([, bytes]) => bytes > LARGE_UNKNOWN_FOLDER_BYTES)
    .map(([path, bytes]) => ({ path, bytes }))
    .sort((left, right) => right.bytes - left.bytes || left.path.localeCompare(right.path));
  const oversizedFiles = files
    .filter(({ file }) => file.size > MAX_UPLOAD_FILE_BYTES)
    .map(({ file, path }) => ({ path, bytes: file.size }))
    .sort((left, right) => left.path.localeCompare(right.path));
  return {
    files,
    omitted,
    summary: {
      totalBytes: selection.totalBytes,
      ignoredBytes: Math.max(0, selection.totalBytes - uploadBytes),
      uploadBytes,
      largeFolders,
      oversizedFiles,
      blocked: uploadBytes > MAX_UPLOAD_TOTAL_BYTES || oversizedFiles.length > 0,
    },
  };
}

export async function diagnoseFolder(files: SelectedFile[]): Promise<FolderDiagnosis> {
  const paths = new Set(files.map(({ path }) => path));
  if (
    paths.has("build/client/index.html")
    && [...paths].some((path) => path.startsWith("build/server/"))
  ) {
    return {
      kind: "unsupported",
      label: "This app still needs a client-only build",
      publishRoot: "",
      explanation: "This React Router build includes a server runtime. Set ssr: false, make a new static build, and choose the project folder again.",
    };
  }
  const source = await sourceBuildDiagnosis(files);
  if (source?.kind === "source-runtime") return source;
  const outputRoot = [
    "build/client",
    ".output/public",
    "dist",
    "build",
    "out",
    "public",
    "_site",
  ].find((root) => paths.has(`${root}/index.html`));
  if (outputRoot) {
    return {
      kind: "prebuilt-static",
      label: "Built app, ready to share",
      publishRoot: outputRoot,
      explanation: `We found the finished site in ${outputRoot}. Doorway will publish those files without running project code.`,
    };
  }
  if (paths.has("index.html")) {
    if (source) return source;
    return {
      kind: "plain-static",
      label: "Website, ready to share",
      publishRoot: ".",
      explanation: "We found a finished website. No project code will run during publishing.",
    };
  }
  if (source) return source;
  return {
    kind: "unsupported",
    label: "This folder needs one more step",
    publishRoot: "",
    explanation: "Ask Claude or Codex to make a static build, then choose the project folder again. Doorway looks for index.html, dist, build, out, build/client, .output/public, public, or _site.",
  };
}

async function sourceBuildDiagnosis(files: SelectedFile[]): Promise<FolderDiagnosis | null> {
  const packageFile = files.find(({ path }) => path === "package.json");
  const hugoConfig = files.find(({ path }) => HUGO_CONFIG_PATHS.has(path));
  if (!packageFile && !hugoConfig) return null;
  const snapshot: Record<string, string> = {};
  if (hugoConfig) {
    for (const { file, path } of files) snapshot[path] = await readText(file);
  } else if (packageFile) {
    snapshot["package.json"] = await readText(packageFile.file);
  }
  for (const { file, path } of files) {
    if (hugoConfig) continue;
    if (
      path === "package-lock.json"
      || path === "pnpm-lock.yaml"
      || path === "yarn.lock"
      || path.startsWith("bun.lock")
      || path === ".npmrc"
      || path === ".pnpmfile.cjs"
      || path === ".pnpmfile.js"
      || path === "pnpm-workspace.yaml"
    ) {
      snapshot[path] = "present";
    } else if (/^(?:astro|next|react-router)\.config(?:\.[^/]+)?$/.test(path)) {
      snapshot[path] = await readText(file);
    }
  }
  const plan = detectSourceBuild(snapshot);
  if (!plan.supported) {
    if (plan.code === "SOURCE_BUILD_SERVER_RUNTIME") {
      if (!hasNextDependency(snapshot["package.json"] ?? "")) {
        return {
          kind: "unsupported",
          label: "This server app needs an enabled Doorway runtime",
          publishRoot: "",
          explanation: "Doorway managed server runtimes currently support Next.js apps. Static Astro and React Router apps can still use a private source build.",
        };
      }
      return {
        kind: "source-runtime",
        label: "Full-stack app, ready for Doorway review",
        publishRoot: ".",
        explanation: "Doorway will analyze external connections in an isolated service after upload, then ask you to confirm the detected calls and credentials before activation.",
      };
    }
    if (
      plan.code === "SOURCE_BUILD_FRAMEWORK_UNSUPPORTED"
      && !files.some(({ path }) => /^(?:vite|astro|next|react-router)\.config(?:\.[^/]+)?$/.test(path))
    ) return null;
    return {
      kind: "unsupported",
      label: "This source project needs a small change",
      publishRoot: "",
      explanation: plan.message,
    };
  }
  return {
    kind: "source-static",
    label: plan.framework === "hugo"
      ? "Hugo source, ready for a private build"
      : "Source app, ready for a private build",
    publishRoot: ".",
    explanation: plan.framework === "hugo"
      ? `Doorway can run the pinned Hugo Extended builder and create the ${plan.publishDirectory} static site inside a temporary isolated container.`
      : `Doorway can install the locked ${plan.packageManager} dependencies and create the ${plan.publishDirectory} static site inside a temporary isolated container.`,
  };
}

function hasNextDependency(packageJson: string): boolean {
  try {
    const parsed: unknown = JSON.parse(packageJson);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return false;
    const manifest = parsed as {
      dependencies?: Record<string, unknown>;
      devDependencies?: Record<string, unknown>;
      peerDependencies?: Record<string, unknown>;
    };
    return manifest.dependencies?.next !== undefined
      || manifest.devDependencies?.next !== undefined
      || manifest.peerDependencies?.next !== undefined;
  } catch {
    return false;
  }
}

async function readText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(String(reader.result ?? "")));
    reader.addEventListener("error", () => reject(reader.error ?? new Error("Could not read project metadata.")));
    reader.readAsText(file);
  });
}

export function filesForPublish(files: SelectedFile[], root: string): SelectedFile[] {
  if (root === ".") return files;
  const prefix = `${root}/`;
  return files.filter(({ path }) => path.startsWith(prefix));
}
